import { CxPortalWhiteIcon } from '@@/icons';
import { SideNavigationProps } from '@cloudscape-design/components';
import { HomeIcon } from 'lucide-react';
import React from 'react';

interface NavItem {
  icon: React.ReactElement<{ className?: string }> | undefined;
  label: string;
  path: string;
  subModules?: {
    label: string;
    path: string;
  }[];
}

export interface PermissionMapping<T extends string> {
  permissionSetMapping: Partial<Record<T, string[]>>;
  urlSignature: string;
  showInNavbar: boolean;
  label?: string;
}

export interface ModuleAttribute<T extends string> {
  moduleSectionName: string;
  moduleUrlPart: string; //module url part
  modulePermissions: PermissionMapping<T>[];
  moduleIcon?: React.ReactElement<{ className?: string }>;
}

const getUrlSignatures = (url: string) => {
  const urlParts = url.split('/');
  let urlPartIndex = 3;
  let urlPathParams = [''];
  while (urlParts[urlPartIndex]) {
    const newUrlPathParams = urlPathParams.reduce((acc, curr) => {
      acc.push(`${curr}/${urlParts[urlPartIndex]}`);
      acc.push(`${curr}/*`);
      return acc;
    }, [] as string[]);
    urlPathParams = newUrlPathParams;
    ++urlPartIndex;
  }
  return urlPathParams;
};

export const determineUserPermissions = <T extends string>(
  userPermissions: string[],
  url: string,
  permissionMappings: PermissionMapping<T>[]
): Set<T> => {
  try {
    const potentialUrlSignatures = getUrlSignatures(url);
    const pathRequiredPermissions = permissionMappings.find((mapping) =>
      potentialUrlSignatures.includes(mapping.urlSignature)
    );
    if (!pathRequiredPermissions) return new Set();
    return (Object.entries(pathRequiredPermissions.permissionSetMapping) as [T, string[]][]).reduce(
      (acc, [role, requiredPermissions]) => {
        if (userPermissions.some((userPermission) => (requiredPermissions as string[]).includes(userPermission))) {
          acc.add(role);
          return acc;
        }
        return acc;
      },
      new Set<T>()
    );
  } catch (error) {
    console.error('There was a problem establishing permissions: ', String(error));
    return new Set();
  }
};

const getFullUrlPath = (instanceAlias: string, moduleName: string, urlPathSignature: string) => {
  return `/portal/${moduleName}${urlPathSignature}?instance=${instanceAlias}`;
};

export const mapModulesToSideNavigaton = <T extends string>(
  moduleAttributeMappings: ModuleAttribute<T>[],
  userPermissions: string[],
  currentInstanceAlias: string
) => {
  const userPermissionSet = new Set(userPermissions);
  return moduleAttributeMappings.reduce((acc, curr) => {
    const sectionItems = mapModulePermissionsToSideNavItems(curr, currentInstanceAlias, userPermissionSet);
    if (sectionItems.length === 0) return acc;
    acc.push(formatSideNavSection(sectionItems, curr.moduleSectionName));
    return acc;
  }, [] as SideNavigationProps.Section[]);
};

const formatSideNavSection = (
  items: SideNavigationProps.Item[],
  moduleSectionName: string
): SideNavigationProps.Section => {
  return {
    type: 'section',
    text: moduleSectionName,
    items
  };
};

const mapModulePermissionsToSideNavItems = <T extends string>(
  moduleAttributes: ModuleAttribute<T>,
  currentInstanceAlias: string,
  userPermissions: Set<string>
): SideNavigationProps.Item[] => {
  return moduleAttributes.modulePermissions.reduce((acc, curr) => {
    if (!curr.showInNavbar) return acc;
    if (
      Object.values(curr.permissionSetMapping).some((requiredPermissions) =>
        (requiredPermissions as string[]).some((requiredPermission: string) => userPermissions.has(requiredPermission))
      )
    ) {
      acc.push({
        type: 'link',
        text: curr.label || '',
        href: getFullUrlPath(currentInstanceAlias, moduleAttributes.moduleUrlPart, curr.urlSignature)
      });
    }
    return acc;
  }, [] as SideNavigationProps.Item[]);
};

////////////////////////////////////////////////////////////

const baseNavItems = [
  {
    icon: <HomeIcon />,
    label: 'Home | CxCentral ',
    path: '/'
  },
  {
    icon: <CxPortalWhiteIcon />,
    label: 'CxPortal',
    path: '/portal'
  }
];

export const mapShadcnModulesToNavItems = <T extends string>(
  moduleAttributeMappings: ModuleAttribute<T>[],
  userPermissions: string[]
): NavItem[] => {
  const userPermissionSet = new Set(userPermissions);
  return moduleAttributeMappings.reduce(
    (acc, curr) => {
      const sectionItems = mapShadcnModulePermissionsToNavItems(curr, userPermissionSet);
      if (sectionItems.length === 0) return acc;
      acc.push({
        icon: curr.moduleIcon,
        label: curr.moduleSectionName,
        subModules: sectionItems,
        path: curr.moduleUrlPart
      });
      return acc;
    },
    [...baseNavItems] as NavItem[]
  );
};

const mapShadcnModulePermissionsToNavItems = <T extends string>(
  moduleAttributes: ModuleAttribute<T>,
  userPermissions: Set<string>
): SubModuleItem[] => {
  return moduleAttributes.modulePermissions.reduce((acc, curr) => {
    if (!curr.showInNavbar) return acc;
    if (
      Object.values(curr.permissionSetMapping).some((requiredPermissions) =>
        (requiredPermissions as string[]).some((requiredPermission: string) => userPermissions.has(requiredPermission))
      )
    ) {
      acc.push({
        label: curr.label || '',
        path: curr.urlSignature
      });
    }
    return acc;
  }, [] as SubModuleItem[]);
};

export interface SubModuleItem {
  label: string;
  path: string;
}
